import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { API } from '@/App';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { ArrowLeft, Dumbbell, Plus, Trash2, Trophy, ChevronRight, Flame, TrendingUp, X, ArrowUpDown, Calendar } from 'lucide-react';

function ProgressChart({ registros }) {
  if (!registros || registros.length < 2) return null;
  const data = [...registros].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
  const pesos = data.map(r => r.peso);
  const max = Math.max(...pesos);
  const min = Math.min(...pesos);
  const range = max - min || 1;
  const w = 400;
  const h = 120;
  const pad = { t: 15, b: 25, l: 10, r: 10 };
  const chartW = w - pad.l - pad.r;
  const chartH = h - pad.t - pad.b;

  const points = data.map((r, i) => ({
    x: pad.l + (i / (data.length - 1)) * chartW,
    y: pad.t + chartH - ((r.peso - min) / range) * chartH,
    peso: r.peso,
    fecha: r.fecha
  }));

  const line = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
  const area = `${line} L ${points[points.length - 1].x} ${pad.t + chartH} L ${points[0].x} ${pad.t + chartH} Z`;

  return (
    <div className="bg-gray-900 rounded-xl border border-gray-800 p-3" data-testid="progress-chart">
      <p className="text-gray-400 text-xs mb-2 flex items-center gap-1"><TrendingUp className="w-3 h-3" /> Evolucion del peso</p>
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full">
        <defs>
          <linearGradient id="areaGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#FFD700" stopOpacity="0.3" />
            <stop offset="100%" stopColor="#FFD700" stopOpacity="0" />
          </linearGradient>
        </defs>
        {/* Grid lines */}
        {[0, 0.25, 0.5, 0.75, 1].map(frac => (
          <line key={frac} x1={pad.l} x2={w - pad.r} y1={pad.t + chartH * (1 - frac)} y2={pad.t + chartH * (1 - frac)}
            stroke="#333" strokeWidth="0.5" />
        ))}
        {/* Area */}
        <path d={area} fill="url(#areaGrad)" />
        {/* Line */}
        <path d={line} fill="none" stroke="#FFD700" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
        {/* Points */}
        {points.map((p, i) => (
          <g key={i}>
            <circle cx={p.x} cy={p.y} r="4" fill="#0f0f10" stroke="#FFD700" strokeWidth="2" />
            {(i === 0 || i === points.length - 1 || p.peso === max) && (
              <text x={p.x} y={p.y - 8} textAnchor="middle" fill={p.peso === max ? '#FFD700' : '#999'} fontSize="10" fontWeight="bold">
                {p.peso}
              </text>
            )}
          </g>
        ))}
        {/* X axis labels */}
        {points.filter((_, i) => i === 0 || i === points.length - 1).map((p, i) => (
          <text key={i} x={p.x} y={h - 3} textAnchor="middle" fill="#666" fontSize="9">
            {new Date(p.fecha).toLocaleDateString('es-ES', { day: '2-digit', month: 'short' })}
          </text>
        ))}
        {/* Min/Max labels */}
        <text x={w - pad.r + 2} y={pad.t + 4} fill="#666" fontSize="9" textAnchor="start">{max}</text>
        <text x={w - pad.r + 2} y={pad.t + chartH + 4} fill="#666" fontSize="9" textAnchor="start">{min}</text>
      </svg>
    </div>
  );
}

function ControlSesiones() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isPro = user?.es_pro || user?.role === 'entrenador';

  const [ejercicios, setEjercicios] = useState([]);
  const [selectedEjercicio, setSelectedEjercicio] = useState(null);
  const [registros, setRegistros] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showNewForm, setShowNewForm] = useState(false);
  const [nuevoNombre, setNuevoNombre] = useState('');
  const [peso, setPeso] = useState('');
  const [reps, setReps] = useState('');
  const [saving, setSaving] = useState(false);
  const [newRecord, setNewRecord] = useState(null);
  const [sortMode, setSortMode] = useState('peso'); // 'peso' | 'fecha'

  const pesoRef = useRef(null);

  useEffect(() => {
    if (!isPro) { navigate('/'); return; }
    fetchEjercicios();
  }, []);

  const fetchEjercicios = async () => {
    try {
      const res = await axios.get(`${API}/ejercicios`);
      setEjercicios(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const crearEjercicio = async () => {
    if (!nuevoNombre.trim()) return;
    try {
      const res = await axios.post(`${API}/ejercicios`, { nombre: nuevoNombre.trim() });
      setEjercicios(prev => [res.data, ...prev]);
      setNuevoNombre('');
      setShowNewForm(false);
      selectEjercicio(res.data);
    } catch (e) {
      alert(e.response?.data?.detail || 'Error');
    }
  };

  const eliminarEjercicio = async (id) => {
    if (!window.confirm('Eliminar ejercicio y todo su historial?')) return;
    try {
      await axios.delete(`${API}/ejercicios/${id}`);
      setEjercicios(prev => prev.filter(e => e.id !== id));
      if (selectedEjercicio?.id === id) { setSelectedEjercicio(null); setRegistros([]); }
    } catch (e) { console.error(e); }
  };

  const selectEjercicio = async (ej) => {
    setSelectedEjercicio(ej);
    setNewRecord(null);
    try {
      const res = await axios.get(`${API}/ejercicios/${ej.id}/registros`);
      setRegistros(res.data);
    } catch (e) { console.error(e); }
    setTimeout(() => pesoRef.current?.focus(), 200);
  };

  const addRegistro = async () => {
    if (!peso || !reps || saving) return;
    setSaving(true);
    setNewRecord(null);
    try {
      const res = await axios.post(`${API}/ejercicios/${selectedEjercicio.id}/registros`, {
        peso: parseFloat(peso),
        reps: parseInt(reps)
      });
      if (res.data.nuevo_record) {
        setNewRecord(res.data);
        setSelectedEjercicio(prev => ({ ...prev, record_peso: res.data.peso, record_reps: res.data.reps }));
      }
      setRegistros(prev => {
        const updated = [res.data, ...prev];
        updated.sort((a, b) => b.peso - a.peso);
        return updated;
      });
      setEjercicios(prev => prev.map(e =>
        e.id === selectedEjercicio.id
          ? { ...e, total_registros: e.total_registros + 1, record_peso: res.data.nuevo_record ? res.data.peso : e.record_peso }
          : e
      ));
      setPeso('');
      setReps('');
      pesoRef.current?.focus();
    } catch (e) {
      alert(e.response?.data?.detail || 'Error');
    } finally { setSaving(false); }
  };

  if (loading) {
    return <div className="min-h-screen bg-dark-mk flex items-center justify-center"><div className="text-gold">Cargando...</div></div>;
  }

  // Detail view
  if (selectedEjercicio) {
    return (
      <div className="min-h-screen bg-dark-mk p-4 pb-24" data-testid="control-detail">
        <div className="max-w-lg mx-auto space-y-5">
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={() => { setSelectedEjercicio(null); setRegistros([]); setNewRecord(null); }} className="text-gray-400 hover:text-white">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="flex-1 min-w-0">
              <h1 className="text-2xl font-bold text-white truncate">{selectedEjercicio.nombre}</h1>
              <p className="text-gray-500 text-sm">{selectedEjercicio.total_registros || registros.length} registros</p>
            </div>
          </div>

          {/* Record card */}
          <Card className="bg-gradient-to-r from-amber-900/30 to-yellow-900/20 border-gold/30">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-14 h-14 rounded-full bg-gold/20 flex items-center justify-center flex-shrink-0">
                <Trophy className="w-7 h-7 text-gold" />
              </div>
              <div>
                <p className="text-gray-400 text-xs uppercase tracking-wider">Record personal</p>
                <p className="text-3xl font-black text-gold">{selectedEjercicio.record_peso || 0} kg</p>
                {selectedEjercicio.record_reps > 0 && (
                  <p className="text-gray-400 text-sm">{selectedEjercicio.record_reps} reps</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* New record celebration */}
          {newRecord && (
            <div className="bg-gradient-to-r from-orange-600 to-red-600 rounded-xl p-4 text-center animate-bounce" data-testid="new-record-alert">
              <Flame className="w-10 h-10 text-yellow-300 mx-auto mb-1" />
              <p className="text-white text-xl font-black">NUEVO RECORD!</p>
              <p className="text-yellow-200 text-2xl font-mono font-bold">{newRecord.peso} kg x {newRecord.reps}</p>
              <button onClick={() => setNewRecord(null)} className="mt-2 text-white/60 text-xs underline">cerrar</button>
            </div>
          )}

          {/* Quick input */}
          <Card className="bg-gray-900 border-gray-700">
            <CardContent className="p-4 space-y-3">
              <p className="text-white font-medium text-sm">Registrar serie</p>
              <div className="flex gap-2">
                <div className="flex-1">
                  <label className="text-gray-500 text-xs block mb-1">Peso (kg)</label>
                  <Input ref={pesoRef} type="number" value={peso} onChange={(e) => setPeso(e.target.value)}
                    placeholder="0" min="0" step="0.5"
                    className="bg-gray-800 border-gray-700 text-white text-center text-2xl h-14 font-mono"
                    data-testid="input-peso"
                    onKeyDown={(e) => e.key === 'Enter' && document.querySelector('[data-testid="input-reps"]')?.focus()} />
                </div>
                <div className="flex items-end pb-0.5 text-gray-600 text-2xl font-bold">x</div>
                <div className="flex-1">
                  <label className="text-gray-500 text-xs block mb-1">Reps</label>
                  <Input type="number" value={reps} onChange={(e) => setReps(e.target.value)}
                    placeholder="0" min="1"
                    className="bg-gray-800 border-gray-700 text-white text-center text-2xl h-14 font-mono"
                    data-testid="input-reps"
                    onKeyDown={(e) => e.key === 'Enter' && addRegistro()} />
                </div>
              </div>
              <Button onClick={addRegistro} disabled={!peso || !reps || saving}
                className="w-full h-12 bg-gold text-black hover:bg-gold/80 font-bold text-lg disabled:opacity-30"
                data-testid="btn-registrar">
                {saving ? '...' : 'Registrar'}
              </Button>
            </CardContent>
          </Card>

          {/* Progress chart */}
          <ProgressChart registros={registros} />

          {/* History */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-gray-400 text-sm font-medium flex items-center gap-2">
                <TrendingUp className="w-4 h-4" /> Historial
              </p>
              <button onClick={() => setSortMode(prev => prev === 'peso' ? 'fecha' : 'peso')}
                className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gold transition-colors bg-gray-800 px-2.5 py-1.5 rounded-lg"
                data-testid="sort-toggle">
                {sortMode === 'peso' ? <ArrowUpDown className="w-3 h-3" /> : <Calendar className="w-3 h-3" />}
                {sortMode === 'peso' ? 'Mayor peso' : 'Cronologico'}
              </button>
            </div>
            {registros.length === 0 ? (
              <p className="text-gray-600 text-center py-8 text-sm">Sin registros aun. Registra tu primera serie!</p>
            ) : (
              [...registros].sort((a, b) => 
                sortMode === 'peso' ? b.peso - a.peso : new Date(b.created_at) - new Date(a.created_at)
              ).map((r, i, sorted) => {
                const isTop = sortMode === 'peso' && i === 0;
                return (
                <div key={r.id}
                  className={`flex items-center gap-3 p-3 rounded-lg transition-all ${
                    isTop ? 'bg-gold/10 border border-gold/30' : 'bg-gray-900 border border-gray-800'
                  } ${r.is_record ? 'ring-1 ring-gold/40' : ''}`}
                  data-testid={`registro-${r.id}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold ${
                    isTop ? 'bg-gold text-black' : 'bg-gray-800 text-gray-500'
                  }`}>
                    {isTop ? <Trophy className="w-4 h-4" /> : sortMode === 'peso' ? i + 1 : <Calendar className="w-3 h-3" />}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-baseline gap-2">
                      <span className={`text-xl font-black font-mono ${isTop ? 'text-gold' : 'text-white'}`}>{r.peso} kg</span>
                      <span className="text-gray-500 text-sm">x {r.reps} reps</span>
                    </div>
                  </div>
                  <span className="text-gray-600 text-xs">{new Date(r.fecha).toLocaleDateString('es-ES', { day: '2-digit', month: 'short' })}</span>
                  {r.is_record && <Flame className="w-4 h-4 text-orange-400 flex-shrink-0" />}
                </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    );
  }

  // List view
  return (
    <div className="min-h-screen bg-dark-mk p-4 pb-20" data-testid="control-sesiones-page">
      <div className="max-w-lg mx-auto space-y-5">
        <div className="flex items-center gap-3">
          <Button variant="ghost" onClick={() => navigate('/')} className="text-gray-400 hover:text-white">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-gold flex items-center gap-2">
              <Dumbbell className="h-8 w-8" />
              Control Sesiones
            </h1>
            <p className="text-gray-500 text-sm">Tu progreso en fuerza</p>
          </div>
        </div>

        {/* New exercise form */}
        {showNewForm ? (
          <Card className="bg-gray-900 border-gold/30">
            <CardContent className="p-4 space-y-3">
              <p className="text-white font-medium">Nuevo ejercicio</p>
              <Input value={nuevoNombre} onChange={(e) => setNuevoNombre(e.target.value)}
                placeholder="Ej: Press banca, Sentadilla, Peso muerto..."
                className="bg-gray-800 border-gray-700 text-white"
                data-testid="input-nuevo-ejercicio"
                autoFocus
                onKeyDown={(e) => e.key === 'Enter' && crearEjercicio()} />
              <div className="flex gap-2">
                <Button onClick={crearEjercicio} className="flex-1 bg-gold text-black hover:bg-gold/80 font-bold" data-testid="btn-crear-ejercicio">
                  Crear
                </Button>
                <Button variant="ghost" onClick={() => { setShowNewForm(false); setNuevoNombre(''); }} className="text-gray-400">
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Button onClick={() => setShowNewForm(true)}
            className="w-full h-14 bg-gray-900 hover:bg-gray-800 border border-dashed border-gray-700 text-gray-400 hover:text-gold hover:border-gold/50 transition-colors"
            data-testid="btn-new-exercise">
            <Plus className="w-5 h-5 mr-2" /> Nuevo ejercicio
          </Button>
        )}

        {/* Exercise list */}
        <div className="space-y-2">
          {ejercicios.length === 0 && !showNewForm ? (
            <div className="text-center py-16 space-y-3">
              <Dumbbell className="w-16 h-16 text-gray-700 mx-auto" />
              <p className="text-gray-500">Aun no tienes ejercicios</p>
              <p className="text-gray-600 text-sm">Crea tu primer ejercicio para empezar a registrar tu progreso</p>
            </div>
          ) : (
            ejercicios.map((ej) => (
              <div key={ej.id}
                className="flex items-center gap-3 p-4 bg-gray-900 rounded-xl border border-gray-800 hover:border-gray-700 cursor-pointer transition-all group"
                onClick={() => selectEjercicio(ej)}
                data-testid={`ejercicio-${ej.id}`}>
                <div className="w-12 h-12 rounded-xl bg-gray-800 group-hover:bg-gold/20 flex items-center justify-center flex-shrink-0 transition-colors">
                  <Dumbbell className="w-6 h-6 text-gray-500 group-hover:text-gold transition-colors" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white font-bold truncate">{ej.nombre}</p>
                  <div className="flex items-center gap-3 text-xs text-gray-500">
                    {ej.record_peso > 0 && (
                      <span className="flex items-center gap-1 text-gold">
                        <Trophy className="w-3 h-3" /> {ej.record_peso} kg
                      </span>
                    )}
                    <span>{ej.total_registros} registros</span>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={(e) => { e.stopPropagation(); eliminarEjercicio(ej.id); }}
                    className="p-2 text-gray-700 hover:text-red-400 transition-colors opacity-0 group-hover:opacity-100"
                    data-testid={`delete-${ej.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                  <ChevronRight className="w-5 h-5 text-gray-700 group-hover:text-gold transition-colors" />
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

export default ControlSesiones;
